import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CoopValidators } from '@core/helpers/validators.helper';
import { DatiBilancioBisFormValidators } from './validators/dati-bilancio-bis.form-validators';
import { DatiBilancioBisWarningsService } from './warnings/dati-bilancio-bis.warnings';
import { DatiBilancioBisFieldValidators } from './validators/dati-bilancio-bis.field-validators';
import { DatiBilancioBisFormListeners } from './listeners/dati-bilancio-bis.form-listeners';
import { BaseRevisioneFormService } from '../../abstracts/base-revisione-form.service';
import { formPaths } from '@shared/constants/form-paths.const';
import { MyVerbale } from '../../models/interfaces/revisione.interface';
import {
    buildContrattoSocCertificazioneFormArray,
    buildCopiaBilancioAllegatoFormArray,
    buildNotaIntegrativaAllegataFormArray,
    buildRelazCertifAllegataFormArray,
} from '../dati-bilancio/common/builders/dati-bilancio-form-array.builder';

@Injectable({
    providedIn: 'root',
})
export class DatiBilancioBisFormService extends BaseRevisioneFormService<DatiBilancioBisWarningsService, DatiBilancioBisFieldValidators> {
    constructor(protected validators: DatiBilancioBisFieldValidators, protected warningsService: DatiBilancioBisWarningsService) {
        super(warningsService, validators);
    }

    override get dependencyPaths(): Set<string> {
        return new Set([
            // Natura mutualistica
            'my_numBilanciNelDBCentrale',

            // Dati identificativi
            `${formPaths.datiIdentificativi}.my_DenominazEnte`,

            // Bilancio
            `${formPaths.bilancio}.my_NumBilanciInExcel`,
            `${formPaths.bilancio}.my_RelazCertifAllegata.my_ElRelazCertifAllegata`,
            `${formPaths.bilancio}.my_ContrattoSocietaCertificazione.my_ElContrattoSocietaCertificazione`,
            `${formPaths.bilancio}.my_CopiaBilancioAllegato.my_ElCopiaBilancioAllegato`,
            `${formPaths.bilancio}.my_NotaIntegrativaAllegata.my_ElNotaIntegrativaAllegata`,
            `${formPaths.bilancio}.my_NoteAlBilancio`,
            `${formPaths.bilancio}.my_BilancioAllegato`,
            `${formPaths.bilancio}.my_CkTotAttivoAnnoXBRL`,
            `${formPaths.bilancio}.my_CkTotAttivoAnno1XBRL`,
            `${formPaths.bilancio}.my_CkPatrNettoAnnoXBRL`,
            `${formPaths.bilancio}.my_CkPatrNettoAnno1XBRL`,
        ]);
    }

    override initListener(): void {
        new DatiBilancioBisFormListeners(this._form, this.store).setListeners();
    }

    protected override afterPopulateForm(myVerbale: MyVerbale): void {
        buildRelazCertifAllegataFormArray(this.fb, myVerbale, this._form, false);
        buildContrattoSocCertificazioneFormArray(this.fb, myVerbale, this._form, false);
        buildNotaIntegrativaAllegataFormArray(this.fb, myVerbale, this._form);
        buildCopiaBilancioAllegatoFormArray(this.fb, myVerbale, this._form);
    }

    protected override initForm(): FormGroup {
        return this.fb.group(
            {
                my_numBilanciNelDBCentrale: '',
                my_SezioneI__Rilevazione: this.fb.group({
                    my_DatiIdentificativi: this.fb.group({
                        my_DenominazEnte: '',
                    }),

                    my_EffettivitaBaseSociale: this.fb.group({
                        my_ChiusuraEsercizio: '',
                    }),
                    my_Bilancio: this.fb.group({
                        my_NumBilanciInExcel: '',
                        my_RelazCertifAllegata: this.fb.group({
                            my_ElRelazCertifAllegata: this.fb.array([]),
                        }),
                        my_ContrattoSocietaCertificazione: this.fb.group({
                            my_ElContrattoSocietaCertificazione: this.fb.array([]),
                        }),
                        my_CopiaBilancioAllegato: this.fb.group({
                            my_ElCopiaBilancioAllegato: this.fb.array([]),
                        }),
                        my_NotaIntegrativaAllegata: this.fb.group({
                            my_ElNotaIntegrativaAllegata: this.fb.array([]),
                        }),
                        my_NoteAlBilancio: '',
                        my_BilancioAllegato: '',
                        my_CkTotAttivoAnnoXBRL: ['', this.validatorsService.my_checkTotAttivoAnnoXBRL],
                        my_CkTotAttivoAnno1XBRL: ['', this.validatorsService.my_checkTotAttivoAnno1XBRL],
                        my_CkPatrNettoAnnoXBRL: ['', this.validatorsService.my_checkPatrNettoAnnoXBRL],
                        my_CkPatrNettoAnno1XBRL: ['', this.validatorsService.my_checkPatrNettoAnno1XBRL],
                    }),
                    my_DatiBilancio: this.fb.group({
                        my_BilAnno2: ['', CoopValidators.required],
                        my_BilAnno1: ['', CoopValidators.required],
                        my_TipoBilancioAbbr2: ['', CoopValidators.required],
                        my_TipoBilancioAbbr1: '',
                        my_DataChiusura2: ['', this.validatorsService.my_DataChiusura2],
                        my_DataChiusura1: ['', this.validatorsService.my_DataChiusura1],
                        my_DataApprovazione2: ['', CoopValidators.required],
                        my_DataApprovazione1: '',
                        my_StatoPatrimoniale__Attivo: this.fb.group({
                            my_StPaAtAa1: '',
                            my_StPaAtAb1: '',
                            my_StPaAtAc1: '',
                            my_StPaAtAtot1: ['', CoopValidators.required],
                            my_StPaAtBI11: '',
                            my_StPaAtBI21: '',
                            my_StPaAtBI31: '',
                            my_StPaAtBI41: '',
                            my_StPaAtBI51: '',
                            my_StPaAtBI61: '',
                            my_StPaAtBI71: '',
                            my_StPaAtBItot1: '',
                            my_StPaAtBII11: '',
                            my_StPaAtBII21: '',
                            my_StPaAtBII31: '',
                            my_StPaAtBII41: '',
                            my_StPaAtBII51: '',
                            my_StPaAtBIItot1: ['', CoopValidators.required],
                            my_StPaAtBIII1a1: '',
                            my_StPaAtBIII1b1: '',
                            my_StPaAtBIII1c1: '',
                            my_StPaAtBIII1d1: '',
                            my_StPaAtBIII1tot1: '',
                            my_StPaAtBIII2a1: '',
                            my_StPaAtBIII2a11: ['', CoopValidators.required],
                            my_StPaAtBIII2b1: '',
                            my_StPaAtBIII2b11: ['', CoopValidators.required],
                            my_StPaAtBIII2c1: '',
                            my_StPaAtBIII2c11: ['', CoopValidators.required],
                            my_StPaAtBIII2d1: '',
                            my_StPaAtBIII2d11: ['', CoopValidators.required],
                            my_StPaAtBIII2e1: '',
                            my_StPaAtBIII2e11: ['', CoopValidators.required],
                            my_StPaAtBIII2tot1: '',
                            my_StPaAtBIII31: '',
                            my_StPaAtBIII41: '',
                            my_StPaAtBIIItot1: '',
                            my_StPaAtBtot1: '',
                            my_StPaAtCI11: '',
                            my_StPaAtCI21: '',
                            my_StPaAtCI31: '',
                            my_StPaAtCI41: '',
                            my_StPaAtCI51: '',
                            my_StPaAtCItot1: ['', CoopValidators.required],
                            my_StPaAtCII1a1: '',
                            my_StPaAtCII1b1: '',
                            my_StPaAtCII1tot1: '',
                            my_StPaAtCII2a1: '',
                            my_StPaAtCII2b1: '',
                            my_StPaAtCII2tot1: '',
                            my_StPaAtCII3a1: '',
                            my_StPaAtCII3b1: '',
                            my_StPaAtCII3tot1: '',
                            my_StPaAtCII4a1: '',
                            my_StPaAtCII4b1: '',
                            my_StPaAtCII4tot1: '',
                            my_StPaAtCII5a1: '',
                            my_StPaAtCII5b1: '',
                            my_StPaAtCII5tot1: '',
                            my_StPaAtCIItot1: '',
                            my_StPaAtCIII11: '',
                            my_StPaAtCIII21: '',
                            my_StPaAtCIII31: '',
                            my_StPaAtCIII41: '',
                            my_StPaAtCIII51: '',
                            my_StPaAtCIIItot1: ['', CoopValidators.required],
                            my_StPaAtCIV11: '',
                            my_StPaAtCIV21: '',
                            my_StPaAtCIV31: '',
                            my_StPaAtCIVtot1: ['', CoopValidators.required],
                            my_StPaAtCtot1: '',
                            my_StPaAtDa1: '',
                            my_StPaAtDb1: '',
                            my_StPaAtDtot1: ['', CoopValidators.required],
                            my_StPaAtTOT1: ['', CoopValidators.required],
                            my_StPaAtBI1: '',
                            my_StPaAtBII1: '',
                            my_StPaAtBIa1: '',
                            my_StPaAtBIIa1: '',
                            my_StPaAtCIIa1: '',
                            my_StPaAtCIIb1: '',
                            my_StPaAtCII4bisa_new1: '',
                            my_StPaAtCII4bisb_new1: '',
                            my_StPaAtCII4bistot_new1: '',
                            my_StPaAtCII4tera_new1: '',
                            my_StPaAtCII4terb_new1: '',
                            my_StPaAtCII4tertot_new1: '',
                            my_StPaAtCIII3_new1: '',
                            my_StPaAtAa2: '',
                            my_StPaAtAb2: '',
                            my_StPaAtAc2: '',
                            my_StPaAtAtot2: ['', CoopValidators.required],
                            my_StPaAtBI12: '',
                            my_StPaAtBI22: '',
                            my_StPaAtBI32: '',
                            my_StPaAtBI42: '',
                            my_StPaAtBI52: '',
                            my_StPaAtBI62: '',
                            my_StPaAtBI72: '',
                            my_StPaAtBItot2: '',
                            my_StPaAtBII12: '',
                            my_StPaAtBII22: '',
                            my_StPaAtBII32: '',
                            my_StPaAtBII42: '',
                            my_StPaAtBII52: '',
                            my_StPaAtBIItot2: ['', CoopValidators.required],
                            my_StPaAtBIII1a2: '',
                            my_StPaAtBIII1b2: '',
                            my_StPaAtBIII1c2: '',
                            my_StPaAtBIII1d2: '',
                            my_StPaAtBIII1tot2: '',
                            my_StPaAtBIII2a2: '',
                            my_StPaAtBIII2a12: ['', CoopValidators.required],
                            my_StPaAtBIII2b2: '',
                            my_StPaAtBIII2b12: ['', CoopValidators.required],
                            my_StPaAtBIII2c2: '',
                            my_StPaAtBIII2c12: ['', CoopValidators.required],
                            my_StPaAtBIII2d2: '',
                            my_StPaAtBIII2d12: ['', CoopValidators.required],
                            my_StPaAtBIII2e2: '',
                            my_StPaAtBIII2e12: ['', CoopValidators.required],
                            my_StPaAtBIII2tot2: '',
                            my_StPaAtBIII32: '',
                            my_StPaAtBIII42: '',
                            my_StPaAtBIIItot2: '',
                            my_StPaAtBtot2: '',
                            my_StPaAtCI12: '',
                            my_StPaAtCI22: '',
                            my_StPaAtCI32: '',
                            my_StPaAtCI42: '',
                            my_StPaAtCI52: '',
                            my_StPaAtCItot2: ['', CoopValidators.required],
                            my_StPaAtCII1a2: '',
                            my_StPaAtCII1b2: '',
                            my_StPaAtCII1tot2: '',
                            my_StPaAtCII2a2: '',
                            my_StPaAtCII2b2: '',
                            my_StPaAtCII2tot2: '',
                            my_StPaAtCII3a2: '',
                            my_StPaAtCII3b2: '',
                            my_StPaAtCII3tot2: '',
                            my_StPaAtCII4a2: '',
                            my_StPaAtCII4b2: '',
                            my_StPaAtCII4tot2: '',
                            my_StPaAtCII5a2: '',
                            my_StPaAtCII5b2: '',
                            my_StPaAtCII5tot2: '',
                            my_StPaAtCIItot2: '',
                            my_StPaAtCIII12: '',
                            my_StPaAtCIII22: '',
                            my_StPaAtCIII32: '',
                            my_StPaAtCIII42: '',
                            my_StPaAtCIII52: '',
                            my_StPaAtCIIItot2: ['', CoopValidators.required],
                            my_StPaAtCIV12: '',
                            my_StPaAtCIV22: '',
                            my_StPaAtCIV32: '',
                            my_StPaAtCIVtot2: ['', CoopValidators.required],
                            my_StPaAtCtot2: '',
                            my_StPaAtDa2: '',
                            my_StPaAtDb2: '',
                            my_StPaAtDtot2: ['', CoopValidators.required],
                            my_StPaAtTOT2: ['', [CoopValidators.required, this.validatorsService.my_StPaAtTOT2PatrimonioNullo]],
                            my_StPaAtBI2: '',
                            my_StPaAtBII2: '',
                            my_StPaAtBIa2: '',
                            my_StPaAtBIIa2: '',
                            my_StPaAtCIIa2: '',
                            my_StPaAtCIIb2: '',
                            my_StPaAtCII4bisa_new2: '',
                            my_StPaAtCII4bisb_new2: '',
                            my_StPaAtCII4bistot_new2: '',
                            my_StPaAtCII4tera_new2: '',
                            my_StPaAtCII4terb_new2: '',
                            my_StPaAtCII4tertot_new2: '',
                            my_StPaAtCIII3_new2: '',
                            my_StPaAtBII1a1: '',
                            my_StPaAtBII1b1: '',
                            my_StPaAtBII2a1: '',
                            my_StPaAtBII2b1: '',
                            my_StPaAtBII3a1: '',
                            my_StPaAtBII3b1: '',
                            my_StPaAtBII4a1: '',
                            my_StPaAtBII4b1: '',
                            my_StPaAtBIII1dnew1: '',
                            my_StPaAtBIII2dnew1: '',
                            my_StPaAtBIII2d1new1: '',
                            my_StPaAtBIII4new1: '',
                            my_StPaAtCII5a_new1: '',
                            my_StPaAtCII5b_new1: '',
                            my_StPaAtCII5tot_new1: '',
                            my_StPaAtCIII3_newbis1: '',
                            my_StPaAtCIII5_new1: '',
                            my_StPaAtBIIb1: '',
                            my_StPaAtBIIc1: '',
                            my_StPaAtBII1a2: '',
                            my_StPaAtBII1b2: '',
                            my_StPaAtBII2a2: '',
                            my_StPaAtBII2b2: '',
                            my_StPaAtBII3a2: '',
                            my_StPaAtBII3b2: '',
                            my_StPaAtBII4a2: '',
                            my_StPaAtBII4b2: '',
                            my_StPaAtBIII1dnew2: '',
                            my_StPaAtBIII2dnew2: '',
                            my_StPaAtBIII2d1new2: '',
                            my_StPaAtBIII4new2: '',
                            my_StPaAtCII5a_new2: '',
                            my_StPaAtCII5b_new2: '',
                            my_StPaAtCII5tot_new2: '',
                            my_StPaAtCIII3_newbis2: '',
                            my_StPaAtCIII5_new2: '',
                            my_StPaAtBIIb2: '',
                            my_StPaAtBIIc2: '',
                        }),
                        my_StatoPatrimoniale__Passivo: this.fb.group({
                            my_StPaPsAI1: ['', CoopValidators.required],
                            my_StPaPsAII1: ['', CoopValidators.required],
                            my_StPaPsAIII1: ['', CoopValidators.required],
                            my_StPaPsAIV1: ['', CoopValidators.required],
                            my_StPaPsAV1: ['', CoopValidators.required],
                            my_StPaPsAVI1: ['', CoopValidators.required],
                            my_StPaPsAVIIa1: ['', CoopValidators.required],
                            my_StPaPsAVIIb1: ['', CoopValidators.required],
                            my_StPaPsAVIItot1: '',
                            my_StPaPsAVIII1: ['', CoopValidators.required],
                            my_StPaPsAIX1: ['', CoopValidators.required],
                            my_StPaPsAtot1: '',
                            my_StPaPsB11: '',
                            my_StPaPsB21: '',
                            my_StPaPsB31: '',
                            my_StPaPsBtot1: '',
                            my_StPaPsC1: '',
                            my_StPaPsD11: '',
                            my_StPaPsD21: '',
                            my_StPaPsD3a1: '',
                            my_StPaPsD3b1: '',
                            my_StPaPsD3tot1: '',
                            my_StPaPsD4a1: '',
                            my_StPaPsD4a11: '',
                            my_StPaPsD4b1: '',
                            my_StPaPsD4b11: '',
                            my_StPaPsD4tot1: '',
                            my_StPaPsD51: '',
                            my_StPaPsD6a1: '',
                            my_StPaPsD6b1: '',
                            my_StPaPsD6c1: '',
                            my_StPaPsD6d1: '',
                            my_StPaPsD6tot1: '',
                            my_StPaPsD7a1: '',
                            my_StPaPsD7b1: '',
                            my_StPaPsD7tot1: '',
                            my_StPaPsD8a1: '',
                            my_StPaPsD8b1: '',
                            my_StPaPsD8tot1: '',
                            my_StPaPsD9a1: '',
                            my_StPaPsD9b1: '',
                            my_StPaPsD9tot1: '',
                            my_StPaPsD10a1: '',
                            my_StPaPsD10b1: '',
                            my_StPaPsD10tot1: '',
                            my_StPaPsD11a1: '',
                            my_StPaPsD11b1: '',
                            my_StPaPsD11tot1: '',
                            my_StPaPsD12a1: '',
                            my_StPaPsD12b1: '',
                            my_StPaPsD12tot1: '',
                            my_StPaPsD13a1: '',
                            my_StPaPsD13b1: '',
                            my_StPaPsD13tot1: '',
                            my_StPaPsDtot1: '',
                            my_StPaPsEa1: '',
                            my_StPaPsEb1: '',
                            my_StPaPsEtot1: ['', CoopValidators.required],
                            my_StPaPsTOT1: '',
                            my_StPaPsFI1a1: '',
                            my_StPaPsFI1b1: '',
                            my_StPaPsFI1c1: '',
                            my_StPaPsFI1d1: '',
                            my_StPaPsFI1tot1: '',
                            my_StPaPsFI2a1: '',
                            my_StPaPsFI2b1: '',
                            my_StPaPsFI2c1: '',
                            my_StPaPsFI2d1: '',
                            my_StPaPsFI2tot1: '',
                            my_StPaPsFI3a1: '',
                            my_StPaPsFI3b1: '',
                            my_StPaPsFI3c1: '',
                            my_StPaPsFI3d1: '',
                            my_StPaPsFI3tot1: '',
                            my_StPaPsFItot1: '',
                            my_StPaPsFII1: '',
                            my_StPaPsFIII1: '',
                            my_StPaPsFIV1: '',
                            my_StPaPsFV1: '',
                            my_StPaPsFtot1: '',
                            my_StPaPsDa1: '',
                            my_StPaPsDb1: '',
                            my_StPaPsB2a_new1: '',
                            my_StPaPsB2b_new1: '',
                            my_StPaPsD3tot_new1: '',
                            my_StPaPsD5tot_new1: '',
                            my_StPaPsAI2: ['', CoopValidators.required],
                            my_StPaPsAII2: ['', CoopValidators.required],
                            my_StPaPsAIII2: ['', CoopValidators.required],
                            my_StPaPsAIV2: ['', CoopValidators.required],
                            my_StPaPsAV2: ['', CoopValidators.required],
                            my_StPaPsAVI2: ['', CoopValidators.required],
                            my_StPaPsAVIIa2: ['', CoopValidators.required],
                            my_StPaPsAVIIb2: ['', CoopValidators.required],
                            my_StPaPsAVIItot2: '',
                            my_StPaPsAVIII2: ['', CoopValidators.required],
                            my_StPaPsAIX2: ['', CoopValidators.required],
                            my_StPaPsAtot2: '',
                            my_StPaPsB12: '',
                            my_StPaPsB22: '',
                            my_StPaPsB32: '',
                            my_StPaPsBtot2: '',
                            my_StPaPsC2: '',
                            my_StPaPsD12: '',
                            my_StPaPsD22: '',
                            my_StPaPsD3a2: '',
                            my_StPaPsD3b2: '',
                            my_StPaPsD3tot2: '',
                            my_StPaPsD4a2: '',
                            my_StPaPsD4a12: '',
                            my_StPaPsD4b2: '',
                            my_StPaPsD4b12: '',
                            my_StPaPsD4tot2: '',
                            my_StPaPsD52: '',
                            my_StPaPsD6a2: '',
                            my_StPaPsD6b2: '',
                            my_StPaPsD6c2: '',
                            my_StPaPsD6d2: '',
                            my_StPaPsD6tot2: '',
                            my_StPaPsD7a2: '',
                            my_StPaPsD7b2: '',
                            my_StPaPsD7tot2: '',
                            my_StPaPsD8a2: '',
                            my_StPaPsD8b2: '',
                            my_StPaPsD8tot2: '',
                            my_StPaPsD9a2: '',
                            my_StPaPsD9b2: '',
                            my_StPaPsD9tot2: '',
                            my_StPaPsD10a2: '',
                            my_StPaPsD10b2: '',
                            my_StPaPsD10tot2: '',
                            my_StPaPsD11a2: '',
                            my_StPaPsD11b2: '',
                            my_StPaPsD11tot2: '',
                            my_StPaPsD12a2: '',
                            my_StPaPsD12b2: '',
                            my_StPaPsD12tot2: '',
                            my_StPaPsD13a2: '',
                            my_StPaPsD13b2: '',
                            my_StPaPsD13tot2: '',
                            my_StPaPsDtot2: '',
                            my_StPaPsEa2: '',
                            my_StPaPsEb2: '',
                            my_StPaPsEtot2: ['', CoopValidators.required],
                            my_StPaPsTOT2: '',
                            my_StPaPsFI1a2: '',
                            my_StPaPsFI1b2: '',
                            my_StPaPsFI1c2: '',
                            my_StPaPsFI1d2: '',
                            my_StPaPsFI1tot2: '',
                            my_StPaPsFI2a2: '',
                            my_StPaPsFI2b2: '',
                            my_StPaPsFI2c2: '',
                            my_StPaPsFI2d2: '',
                            my_StPaPsFI2tot2: '',
                            my_StPaPsFI3a2: '',
                            my_StPaPsFI3b2: '',
                            my_StPaPsFI3c2: '',
                            my_StPaPsFI3d2: '',
                            my_StPaPsFI3tot2: '',
                            my_StPaPsFItot2: '',
                            my_StPaPsFII2: '',
                            my_StPaPsFIII2: '',
                            my_StPaPsFIV2: '',
                            my_StPaPsFV2: '',
                            my_StPaPsFtot2: '',
                            my_StPaPsDa2: '',
                            my_StPaPsDb2: '',
                            my_StPaPsB2a_new2: '',
                            my_StPaPsB2b_new2: '',
                            my_StPaPsD3tot_new2: '',
                            my_StPaPsD5tot_new2: '',
                            my_StPaPsAVII_new1: '',
                            my_StPaPsAX1: '',
                            my_StPaPsB3_NEW1: '',
                            my_StPaPsD11a_new1: '',
                            my_StPaPsD11b_new1: '',
                            my_StPaPsD11tot_new1: '',
                            my_StPaPsAVII_new2: '',
                            my_StPaPsAX2: '',
                            my_StPaPsB3_NEW2: '',
                            my_StPaPsD11a_new2: '',
                            my_StPaPsD11b_new2: '',
                            my_StPaPsD11tot_new2: '',
                        }),
                        my_ContoEconomico: this.fb.group({
                            my_CoEcA11: ['', CoopValidators.required],
                            my_CoEcA21: '',
                            my_CoEcA31: '',
                            my_CoEcA41: '',
                            my_CoEcA5a1: '',
                            my_CoEcA5b1: '',
                            my_CoEcA5tot1: '',
                            my_CoEcAtot1: '',
                            my_CoEcB6a1: '',
                            my_CoEcB6b1: ['', CoopValidators.required],
                            my_CoEcB6c1: '',
                            my_CoEcB6tot1: ['', CoopValidators.required],
                            my_CoEcB71: ['', CoopValidators.required],
                            my_CoEcB81: '',
                            my_CoEcB9a1: '',
                            my_CoEcB9b1: '',
                            my_CoEcB9c1: '',
                            my_CoEcB9d1: '',
                            my_CoEcB9e1: '',
                            my_CoEcB9tot1: ['', CoopValidators.required],
                            my_CoEcB10a1: '',
                            my_CoEcB10b1: '',
                            my_CoEcB10c1: '',
                            my_CoEcB10dd11: '',
                            my_CoEcB10dd21: '',
                            my_CoEcB10dtot1: '',
                            my_CoEcB10tot1: '',
                            my_CoEcB111: '',
                            my_CoEcB121: '',
                            my_CoEcB131: '',
                            my_CoEcB141: '',
                            my_CoEcBtot1: '',
                            my_CoEcA_B1: '',
                            my_CoEcC15a1: '',
                            my_CoEcC15b1: '',
                            my_CoEcC15c1: '',
                            my_CoEcC15tot1: '',
                            my_CoEcC16aa11: '',
                            my_CoEcC16aa21: '',
                            my_CoEcC16aa31: '',
                            my_CoEcC16aa41: '',
                            my_CoEcC16atot1: '',
                            my_CoEcC16b1: '',
                            my_CoEcC16c1: '',
                            my_CoEcC16dd11: '',
                            my_CoEcC16dd21: '',
                            my_CoEcC16dd31: '',
                            my_CoEcC16dd41: '',
                            my_CoEcC16dtot1: '',
                            my_CoEcC16tot1: '',
                            my_CoEcC17a1: '',
                            my_CoEcC17b1: '',
                            my_CoEcC17c1: '',
                            my_CoEcC17d1: '',
                            my_CoEcC17e1: '',
                            my_CoEcC17tot1: '',
                            my_CoEcCtot1: ['', CoopValidators.required],
                            my_CoEcD18a1: '',
                            my_CoEcD18b1: '',
                            my_CoEcD18c1: '',
                            my_CoEcD18tot1: '',
                            my_CoEcD19a1: '',
                            my_CoEcD19b1: '',
                            my_CoEcD19c1: '',
                            my_CoEcD19tot1: '',
                            my_CoEcDtot1: '',
                            my_CoEcE20a1: '',
                            my_CoEcE20b1: '',
                            my_CoEcE20tot1: '',
                            my_CoEcE21a1: '',
                            my_CoEcE21b1: '',
                            my_CoEcE21c1: '',
                            my_CoEcE21tot1: '',
                            my_CoEcEtot1: '',
                            my_CoEcLORDO1: '',
                            my_CoEcF221: '',
                            my_CoEcF231: '',
                            my_CoEcF24a1: '',
                            my_CoEcF24b1: '',
                            my_CoEcF24tot1: '',
                            my_CoEcF251: '',
                            my_CoEcF261: ['', CoopValidators.required],
                            my_CoEcC17bis_new1: '',
                            my_CoEcF22a_new1: '',
                            my_CoEcF22b_new1: '',
                            my_CoEcF22c_new1: '',
                            my_CoEcA12: ['', CoopValidators.required],
                            my_CoEcA22: '',
                            my_CoEcA32: '',
                            my_CoEcA42: '',
                            my_CoEcA5a2: '',
                            my_CoEcA5b2: '',
                            my_CoEcA5tot2: '',
                            my_CoEcAtot2: '',
                            my_CoEcB6a2: '',
                            my_CoEcB6b2: ['', CoopValidators.required],
                            my_CoEcB6c2: '',
                            my_CoEcB6tot2: ['', CoopValidators.required],
                            my_CoEcB72: ['', CoopValidators.required],
                            my_CoEcB82: '',
                            my_CoEcB9a2: '',
                            my_CoEcB9b2: '',
                            my_CoEcB9c2: '',
                            my_CoEcB9d2: '',
                            my_CoEcB9e2: '',
                            my_CoEcB9tot2: ['', CoopValidators.required],
                            my_CoEcB10a2: '',
                            my_CoEcB10b2: '',
                            my_CoEcB10c2: '',
                            my_CoEcB10dd12: '',
                            my_CoEcB10dd22: '',
                            my_CoEcB10dtot2: '',
                            my_CoEcB10tot2: '',
                            my_CoEcB112: '',
                            my_CoEcB122: '',
                            my_CoEcB132: '',
                            my_CoEcB142: '',
                            my_CoEcBtot2: '',
                            my_CoEcA_B2: '',
                            my_CoEcC15a2: '',
                            my_CoEcC15b2: '',
                            my_CoEcC15c2: '',
                            my_CoEcC15tot2: '',
                            my_CoEcC16aa12: '',
                            my_CoEcC16aa22: '',
                            my_CoEcC16aa32: '',
                            my_CoEcC16aa42: '',
                            my_CoEcC16atot2: '',
                            my_CoEcC16b2: '',
                            my_CoEcC16c2: '',
                            my_CoEcC16dd12: '',
                            my_CoEcC16dd22: '',
                            my_CoEcC16dd32: '',
                            my_CoEcC16dd42: '',
                            my_CoEcC16dtot2: '',
                            my_CoEcC16tot2: '',
                            my_CoEcC17a2: '',
                            my_CoEcC17b2: '',
                            my_CoEcC17c2: '',
                            my_CoEcC17d2: '',
                            my_CoEcC17e2: '',
                            my_CoEcC17tot2: '',
                            my_CoEcCtot2: ['', CoopValidators.required],
                            my_CoEcD18a2: '',
                            my_CoEcD18b2: '',
                            my_CoEcD18c2: '',
                            my_CoEcD18tot2: '',
                            my_CoEcD19a2: '',
                            my_CoEcD19b2: '',
                            my_CoEcD19c2: '',
                            my_CoEcD19tot2: '',
                            my_CoEcDtot2: '',
                            my_CoEcE20a2: '',
                            my_CoEcE20b2: '',
                            my_CoEcE20tot2: '',
                            my_CoEcE21a2: '',
                            my_CoEcE21b2: '',
                            my_CoEcE21c2: '',
                            my_CoEcE21tot2: '',
                            my_CoEcEtot2: '',
                            my_CoEcLORDO2: '',
                            my_CoEcF222: '',
                            my_CoEcF232: '',
                            my_CoEcF24a2: '',
                            my_CoEcF24b2: '',
                            my_CoEcF24tot2: '',
                            my_CoEcF252: '',
                            my_CoEcF262: ['', CoopValidators.required],
                            my_CoEcC17bis_new2: '',
                            my_CoEcF22a_new2: '',
                            my_CoEcF22b_new2: '',
                            my_CoEcF22c_new2: '',
                            my_CoEcc15bbis1: '',
                            my_CoEcc15bter1: '',
                            my_CoEcc16aa3bis1: '',
                            my_CoEcC16dd2bis1: '',
                            my_CoEcC16dd2ter1: '',
                            my_CoEcc15bbis2: '',
                            my_CoEcc15bter2: '',
                            my_CoEcc16aa3bis2: '',
                            my_CoEcC16dd2bis2: '',
                            my_CoEcC16dd2ter2: '',
                        }),
                        my_TipoSchemaBil1: 'V',
                        my_TipoSchemaBil2: ['V', CoopValidators.required],
                        my_BilZippato: 'N',
                        my_SecondiAnalisiBilancio: '0',
                        my_RilevaDatiAggiuntiviBilBreve: 'S',
                        my_StatoPatrimoniale__Attivo__AltriDati: this.fb.group({
                            my_BIIICredEntroEX1: '',
                            my_BIIIAltreImm1: '',
                            my_CIIaVersoClienti1: '',
                            my_CIIaVersoAltri1: '',
                            my_BIIICredEntroEX2: '',
                            my_BIIIAltreImm2: '',
                            my_CIIaVersoClienti2: '',
                            my_CIIaVersoAltri2: '',
                        }),
                        my_StatoPatrimoniale__Passivo__AltriDati: this.fb.group({
                            my_DvsSociFinanziam1: '',
                            my_DvsFornitEntroEX1: '',
                            my_DvsBancheAltriFin1: '',
                            my_DAltriDebiti1: '',
                            my_DvsSociFinanziam2: '',
                            my_DvsFornitEntroEX2: '',
                            my_DvsBancheAltriFin2: '',
                            my_DAltriDebiti2: '',
                        }),
                        my_CtrlDatiAggiuntiviBilBreve: 'OK',
                        my_DataApprovazione1_60: '',
                        my_DataApprovazione2_60: '',
                    }),
                }),
            },
            {
                updateOn: 'change',
                validators: DatiBilancioBisFormValidators,
            }
        );
    }
}
